import React from 'react';
import { motion } from 'framer-motion';
import { Zap, ArrowRight, Play, Terminal, Database, Shield, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const ForkMode: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-20 px-8">
      <div className="max-w-7xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Abort Fork Simulation
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-8 h-8 text-yellow-500" />
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-white uppercase">Fork Mode Debugger</h1>
          </div>
          <p className="text-[#525252] font-mono text-lg max-w-2xl leading-relaxed">
            Spin up a local fork from any block height to simulate transactions and test custom calls in real-time.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-8">
            <section className="p-10 rounded-3xl bg-[#0F0F0F] border border-[#262626] flex flex-col gap-8">
              <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#525252]">Network</label>
                  <select className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-yellow-500/50">
                    <option>Ethereum Mainnet</option>
                    <option>Arbitrum One</option>
                    <option>Optimism</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#525252]">Block Number</label>
                  <input 
                    type="text" 
                    placeholder="LATEST" 
                    className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-yellow-500/50"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#525252]">Fork RPC URL</label>
                <input 
                  type="text" 
                  placeholder="https://eth-mainnet.g.alchemy.com/v2/..." 
                  className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-yellow-500/50"
                />
              </div>
              <button className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-8 py-4 rounded-xl transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                <Play className="w-4 h-4 fill-current" />
                Initialize Fork Session
              </button>
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626] flex flex-col justify-between group hover:border-yellow-500/30 transition-all">
                <Terminal className="w-6 h-6 text-yellow-500 mb-6" />
                <h4 className="text-lg font-bold text-white mb-2 uppercase tracking-tight">Simulate Transactions</h4>
                <p className="text-xs text-[#525252] leading-relaxed">Test complex interactions before deploying to mainnet.</p>
              </div>
              <div className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626] flex flex-col justify-between group hover:border-yellow-500/30 transition-all">
                <Database className="w-6 h-6 text-yellow-500 mb-6" />
                <h4 className="text-lg font-bold text-white mb-2 uppercase tracking-tight">Override State</h4>
                <p className="text-xs text-[#525252] leading-relaxed">Modify storage slots and account balances in the fork.</p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-6 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Session Security
              </h3>
              <p className="text-xs text-[#525252] leading-relaxed mb-6 font-mono">
                Fork sessions are isolated and ephemeral. No private keys are ever stored on our servers.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">
                  <div className="w-1 h-1 rounded-full bg-yellow-500" />
                  Local-only simulation
                </div>
                <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">
                  <div className="w-1 h-1 rounded-full bg-yellow-500" />
                  No real gas costs
                </div>
                <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">
                  <div className="w-1 h-1 rounded-full bg-yellow-500" />
                  Instant state resets
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
